import { Reducer, AnyAction } from 'redux';
import { DefaultFiltersCommonState } from './types-store';
import {
  ACTION,
  RequestFilterMetadata, ReceiveFilterMetadata, FailFilterMetadata,
  AddFilters, UpdateFilter, DeleteFilter, DeleteAllFilters,
  UpdateFiltersValidation
} from './action-types';

const reducers: { [indexer: string]: Reducer<DefaultFiltersCommonState> } = {

  [ACTION.REQUEST_FILTER_METADATA]: (state: DefaultFiltersCommonState,
    action: RequestFilterMetadata): DefaultFiltersCommonState => {

    return {
      ...state,
      fetchingFilterMetadata: [
        ...state.fetchingFilterMetadata,
        ...action.domainIds.filter(id => !state.fetchingFilterMetadata.includes(id))
      ]
    };
  },

  [ACTION.RECEIVE_FILTER_METADATA]: (state: DefaultFiltersCommonState,
    action: ReceiveFilterMetadata): DefaultFiltersCommonState => {

    const filterMetadata = action.filterMetadata
      .reduce((prev, filter) => ({
        ...prev,
        [filter.domain.value]: filter
      }), state.filterMetadata);

    return {
      ...state,
      fetchingFilterMetadata: state.fetchingFilterMetadata.filter(id => !action.domainIds.includes(id)),
      filterMetadata
    };
  },

  [ACTION.FAIL_FILTER_METADATA]: (state: DefaultFiltersCommonState,
    action: FailFilterMetadata): DefaultFiltersCommonState => {

    return {
      ...state,
      fetchingFilterMetadata: state.fetchingFilterMetadata.filter(id => !action.domainIds.includes(id))
    };
  },

  [ACTION.ADD_FILTERS]: (
    state: DefaultFiltersCommonState,
    action: AddFilters
  ): DefaultFiltersCommonState => {

    return {
      ...state,
      filters: [
        ...(state.filters ?? []),
        ...action.filters
      ]
    };
  },

  [ACTION.UPDATE_FILTER]: (
    state: DefaultFiltersCommonState,
    action: UpdateFilter
  ): DefaultFiltersCommonState => {

    const stateFilters = state.filters ?? [];
    const existingFilter = stateFilters.find(
      filter => filter.domain?.value === action.domainId
    );

    const { [action.domainId]: _, ...filteredValidation } = state.filterValidation ?? {};
    const validation = Object.keys(action.validation || {}).length > 0
      ? {
        ...filteredValidation,
        [action.domainId]: action.validation
      }
      : filteredValidation;

    return {
      ...state,
      filters: existingFilter
        ? stateFilters.map(filter => filter.domain?.value !== action.domainId
          ? filter
          : action.filter)
        : [
          ...stateFilters,
          action.filter
        ],
      filterValidation: validation
    };
  },

  [ACTION.DELETE_FILTER]: (
    state: DefaultFiltersCommonState,
    action: DeleteFilter
  ): DefaultFiltersCommonState => {

    const activeFilter = state.filters?.find(filter => filter.domain?.value === action.domainId);

    if (!activeFilter) {
      return state;
    }

    const filters = (state.filters ?? [])
      .filter(filter => filter !== activeFilter);
    const { [action.domainId]: _, ...validation } = state.filterValidation;

    return {
      ...state,
      filters,
      filterValidation: validation
    };
  },

  [ACTION.DELETE_ALL_FILTERS]: (
    state: DefaultFiltersCommonState,
    action: DeleteAllFilters
  ): DefaultFiltersCommonState => {

    return {
      ...state,
      filters: [],
      filterValidation: {}
    };
  },

  [ACTION.UPDATE_VALIDATION]: (
    state: DefaultFiltersCommonState,
    action: UpdateFiltersValidation
  ): DefaultFiltersCommonState => {

    return {
      ...state,
      filterValidation: action.filterValidation
    };
  }
};

export const withDefaultFiltersReducer =
  <S extends DefaultFiltersCommonState>(component: string, reducer: Reducer<S>) =>
    (state: S, action: AnyAction) => {
      const r = action.component === component && reducers[action.type];
      return ((r && r(state as any, action)) || reducer(state, action)) as S;
    };
